import type { WatchQueryFetchPolicy } from 'apollo-client';
import type { GraphQLError } from 'graphql';

import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { FetchPolicy } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';

import type { SitePersonalizationQuery_experimentalSitePersonalization_attributes as SitePersonalizationAttributes } from './__types__/SitePersonalizationQuery.ts';
import { useExperimentalSitePersonalizationQuery } from './useExperimentalSitePersonalizationQuery.ts';

export type useSitePersonalizationQueryProps = {
	cloudId: string;
	skip?: boolean;
	fetchPolicy?: WatchQueryFetchPolicy;
};

export let skipQueryIfUnauthorized = false;

export const useSitePersonalizationQuery = ({
	cloudId,
	skip,
	fetchPolicy,
}: {
	cloudId: string;
	skip?: boolean;
	fetchPolicy?: WatchQueryFetchPolicy;
}) => {
	const isLicensed = useIsLicensed();

	const skipOptions =
		skip ||
		!isLicensed ||
		skipQueryIfUnauthorized ||
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR); // SitePersonalizationQuery isn't preloaded and can be skipped during SSR to avoid unnecessary requests

	const { data, loading, error } = useExperimentalSitePersonalizationQuery({
		variables: {
			cloudId,
		},
		fetchPolicy: (fetchPolicy || 'cache-first') as FetchPolicy,
		skip: skipOptions,
		onError(apolloError: ApolloErrorInterop) {
			skipQueryIfUnauthorized = apolloError?.graphQLErrors?.some(
				(graphQLError: GraphQLError) =>
					isStatusCodeError(graphQLError, '401') || isUnauthorizedError(graphQLError),
			);
		},
	});
	const attributes: SitePersonalizationAttributes[] =
		data?.experimentalSitePersonalization?.attributes || [];
	return {
		loading,
		error,
		attributes,
	};
};
